import { useMemo } from 'react';

import { type DocumentNode, gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type { StagePageCursor } from '../../routes/settings/labels/types';
import type {
  NexoyaContentLabelAssignmentConnection,
  NexoyaContentLabelCategory,
  NexoyaContentsByAssignmentStage,
} from '../../types';

import { CONTENT_LABEL_VALUE_REF_FRAGMENT, LABELABLE_CONTENT_FRAGMENT } from './fragments';
import { STAGE_READ_CONTEXT, STAGE_READS_CONNECTION_KEY } from './stageReads';

/**
 * Content Labels v2 — one page of one of a category's four assignment stages.
 *
 * There is no `contentLabelCategory(id)` root field, so a stage is read through
 * `listContentLabelCategories` and the wanted category is picked out of the list
 * client-side. The server therefore answers the requested stage page for EVERY
 * category; each lands on its own category entity in the cache, keyed by the
 * paging arguments, so sibling categories never overwrite each other. The array
 * of categories itself is stored under the stage reads' `@connection` key, apart
 * from the one the category list renders (see `STAGE_READS_CONNECTION_KEY`).
 */

/** The four buckets, named exactly as the fields on `ContentsByAssignmentStage`. */
export type ContentLabelStage = 'labeled' | 'needsDecision' | 'manual' | 'unlabeled';

export const CONTENT_LABEL_STAGES: ContentLabelStage[] = ['labeled', 'needsDecision', 'manual', 'unlabeled'];

export const CONTENT_LABEL_ASSIGNMENT_FRAGMENT = gql`
  fragment ContentLabelAssignmentFields on ContentLabelAssignment {
    labelStatus
    assignment {
      ...ContentLabelValueRef
    }
    matches {
      ...ContentLabelValueRef
    }
    content {
      ...LabelableContentFields
    }
  }
  ${CONTENT_LABEL_VALUE_REF_FRAGMENT}
  ${LABELABLE_CONTENT_FRAGMENT}
`;

export const CONTENT_LABEL_ASSIGNMENT_CONNECTION_FRAGMENT = gql`
  fragment ContentLabelAssignmentConnectionFields on ContentLabelAssignmentConnection {
    edges {
      node {
        ...ContentLabelAssignmentFields
      }
      cursor
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    totalCount
    totalPages
  }
  ${CONTENT_LABEL_ASSIGNMENT_FRAGMENT}
`;

/**
 * One document per stage, on purpose.
 *
 * Every stage is its own connection with its own cursor, so a single document
 * selecting all four would have to share one `$after` between them and page them
 * in lockstep — which is wrong the moment the buckets have different sizes. One
 * stage per document means the paging variables unambiguously belong to the
 * connection being paged.
 */
export const CONTENTS_BY_ASSIGNMENT_STAGE_LABELED_QUERY = gql`
  query ContentsByAssignmentStageLabeled(
    $teamId: Int!
    $portfolioId: Float
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${STAGE_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      contentsByAssignmentStage(portfolioId: $portfolioId) {
        labeled(first: $first, after: $after, last: $last, before: $before) {
          ...ContentLabelAssignmentConnectionFields
        }
      }
    }
  }
  ${CONTENT_LABEL_ASSIGNMENT_CONNECTION_FRAGMENT}
`;

export const CONTENTS_BY_ASSIGNMENT_STAGE_NEEDS_DECISION_QUERY = gql`
  query ContentsByAssignmentStageNeedsDecision(
    $teamId: Int!
    $portfolioId: Float
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${STAGE_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      contentsByAssignmentStage(portfolioId: $portfolioId) {
        needsDecision(first: $first, after: $after, last: $last, before: $before) {
          ...ContentLabelAssignmentConnectionFields
        }
      }
    }
  }
  ${CONTENT_LABEL_ASSIGNMENT_CONNECTION_FRAGMENT}
`;

export const CONTENTS_BY_ASSIGNMENT_STAGE_MANUAL_QUERY = gql`
  query ContentsByAssignmentStageManual(
    $teamId: Int!
    $portfolioId: Float
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${STAGE_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      contentsByAssignmentStage(portfolioId: $portfolioId) {
        manual(first: $first, after: $after, last: $last, before: $before) {
          ...ContentLabelAssignmentConnectionFields
        }
      }
    }
  }
  ${CONTENT_LABEL_ASSIGNMENT_CONNECTION_FRAGMENT}
`;

export const CONTENTS_BY_ASSIGNMENT_STAGE_UNLABELED_QUERY = gql`
  query ContentsByAssignmentStageUnlabeled(
    $teamId: Int!
    $portfolioId: Float
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    listContentLabelCategories(teamId: $teamId) @connection(key: "${STAGE_READS_CONNECTION_KEY}", filter: ["teamId"]) {
      contentLabelCategoryId
      contentsByAssignmentStage(portfolioId: $portfolioId) {
        unlabeled(first: $first, after: $after, last: $last, before: $before) {
          ...ContentLabelAssignmentConnectionFields
        }
      }
    }
  }
  ${CONTENT_LABEL_ASSIGNMENT_CONNECTION_FRAGMENT}
`;

export const CONTENTS_BY_ASSIGNMENT_STAGE_QUERIES: Record<ContentLabelStage, DocumentNode> = {
  labeled: CONTENTS_BY_ASSIGNMENT_STAGE_LABELED_QUERY,
  needsDecision: CONTENTS_BY_ASSIGNMENT_STAGE_NEEDS_DECISION_QUERY,
  manual: CONTENTS_BY_ASSIGNMENT_STAGE_MANUAL_QUERY,
  unlabeled: CONTENTS_BY_ASSIGNMENT_STAGE_UNLABELED_QUERY,
};

/** Only the stage the document asked for comes back, hence `Partial`. */
export type StageCategory = Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'> & {
  contentsByAssignmentStage: Partial<Pick<NexoyaContentsByAssignmentStage, ContentLabelStage>>;
};

export type ContentsByAssignmentStageResponse = {
  listContentLabelCategories: StageCategory[];
};

export type ContentsByAssignmentStageConnection = NexoyaContentLabelAssignmentConnection;

/** Optional and never nullable on purpose — see `toStagePageVariables`: a null here is a server error. */
type StagePageArgs = {
  first?: number;
  after?: string;
  last?: number;
  before?: string;
};

export type ContentsByAssignmentStageVariables = StagePageArgs & {
  teamId: number;
  portfolioId: number | null;
};

/**
 * The paging arguments for one page, with the direction's OTHER pair left
 * UNSENT — `undefined`, never `null` — for two reasons.
 *
 * 1. The server throws on `first` + `before` and on `last` + `after`, so a
 *    forward page must never carry a stale `before` (or the reverse). An
 *    argument that is not provided at all cannot leak either way.
 * 2. The service behind `contentsByAssignmentStage` validates its request BODY
 *    with Zod, where `after`/`before` are `string` and `first`/`last` are
 *    `number`. An explicit null there fails the TYPE rather than reading as
 *    "not paging that way": `{first: 50, after: null, last: null, before: null}`
 *    answered `data: null` with three `invalid_type` errors — `body.after`
 *    expected string, `body.last` expected number, `body.before` expected
 *    string — while `{first: 50}` answered `totalCount: 12` on the same token.
 *
 * `undefined` is the mechanism because both layers below drop it: HttpLink
 * serialises the variables with plain `JSON.stringify`, which omits
 * undefined-valued keys, and graphql-js leaves an argument that was never
 * provided out of the resolver's `args` entirely — so nothing null-shaped
 * reaches the validator.
 *
 * The cache key is still a pure function of the cursor. Apollo builds the field
 * key by canonical-stringifying the coerced arguments
 * (`valueToObjectRepresentation` + `canonicalStringify`), which drops
 * undefined-valued keys too: forward page 1 is `labeled({"first":50})`, page 2
 * `labeled({"after":"102","first":50})`, a backward page
 * `labeled({"before":"103","last":50})`. One cursor still means one key, and no
 * two cursors collide — a backward cursor always carries `before`, so it can
 * never render as a forward key. Only the key STRING got shorter.
 */
export function toStagePageVariables({
  pageSize,
  cursor,
}: {
  pageSize: number;
  cursor: StagePageCursor;
}): StagePageArgs {
  if (cursor.direction === 'backward') {
    return { first: undefined, after: undefined, last: pageSize, before: cursor.before };
  }
  // `cursor.after` is `string | null` on the forward variant, so the coalesce is load-bearing: a null
  // `after` is exactly the "first page" case that was failing validation.
  return { first: pageSize, after: cursor.after ?? undefined, last: undefined, before: undefined };
}

function findStageConnection(
  data: ContentsByAssignmentStageResponse | undefined,
  contentLabelCategoryId: string,
  stage: ContentLabelStage | null,
): ContentsByAssignmentStageConnection | undefined {
  if (!stage) return undefined;
  const category = (data?.listContentLabelCategories ?? []).find(
    (candidate) => candidate.contentLabelCategoryId === contentLabelCategoryId,
  );
  return category?.contentsByAssignmentStage?.[stage];
}

type UseContentsByAssignmentStageOptions = {
  contentLabelCategoryId: string;
  /** Null while no stage table is open: nothing is read. */
  stage: ContentLabelStage | null;
  /** Null asks for the category's whole (team-wide) scope, which is what the SDL's optional arg means. */
  portfolioId: number | null;
  pageSize: number;
  cursor: StagePageCursor;
};

/**
 * One page of one stage of one category — page REPLACE, not append.
 *
 * The page is entirely a function of the variables: moving the cursor re-runs
 * the query with new ones and the table renders that page alone. While it loads,
 * `connection` keeps showing the PREVIOUS page (`data ?? previousData`) with
 * `loading` true, so the table can mark itself stale instead of collapsing to a
 * skeleton on every page turn — `notifyOnNetworkStatusChange` is what makes that
 * loading flip observable at all for a cache-first refetch.
 *
 * Cursors are keyset cursors on `contentId`, so a cursor stays valid when a
 * mutation moves rows between stages; such a mutation asks every stage read on
 * screen again (`mutationEffects.activeStageReads`), so this page refetches with
 * the same variables — as a request of its own, never joined to one sent before
 * the mutation (`STAGE_READ_CONTEXT`).
 */
export function useContentsByAssignmentStageQuery({
  contentLabelCategoryId,
  stage,
  portfolioId,
  pageSize,
  cursor,
}: UseContentsByAssignmentStageOptions) {
  const { teamId } = useTeam();

  const query = useQuery<ContentsByAssignmentStageResponse, ContentsByAssignmentStageVariables>(
    // Any document will do while skipped; `skip` guarantees it is never sent.
    CONTENTS_BY_ASSIGNMENT_STAGE_QUERIES[stage ?? 'labeled'],
    {
      // `portfolioId` stays an explicit null: null IS the team-wide scope here — the resolver reads a
      // falsy id as "do not restrict to a portfolio" — and the cache and every mock tell it apart from
      // an omitted argument. The counts read (queryContentLabelStageCounts.ts) sends it the same way.
      variables: { teamId, portfolioId: portfolioId ?? null, ...toStagePageVariables({ pageSize, cursor }) },
      skip: !teamId || !contentLabelCategoryId || !stage,
      notifyOnNetworkStatusChange: true,
      context: STAGE_READ_CONTEXT,
    },
  );

  const { data, previousData } = query;

  // Another stage's document never carries this stage's field, so previousData
  // from before a stage switch cannot leak into the new stage's table.
  const connection = useMemo(
    () => findStageConnection(data ?? previousData, contentLabelCategoryId, stage),
    [data, previousData, contentLabelCategoryId, stage],
  );

  return { ...query, connection };
}
